import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import {
  MapPin, GraduationCap, Ruler, Play, ExternalLink, Mail, Phone,
  User, Share2, Check, Film, Calendar, ShieldCheck, Info, Quote, MessageSquarePlus, Send,
} from 'lucide-react';
import { API } from './api';

/* ─────────────────────────────────────────────────────────────────────────
   Media helpers
   Clips are hosted by YouTube / Hudl / Vimeo, never on this server, so all we
   do is turn a share URL into an embeddable one.
   ───────────────────────────────────────────────────────────────────────── */

const youTubeId = (url) => {
  const patterns = [
    /(?:youtube\.com\/watch\?(?:.*&)?v=)([\w-]{11})/,
    /(?:youtu\.be\/)([\w-]{11})/,
    /(?:youtube\.com\/embed\/)([\w-]{11})/,
    /(?:youtube\.com\/shorts\/)([\w-]{11})/,
  ];
  for (const re of patterns) {
    const m = url && url.match(re);
    if (m) return m[1];
  }
  return null;
};

const vimeoId = (url) => {
  const m = url && url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  return m ? m[1] : null;
};

/**
 * Returns an iframe src for a clip, or null when the provider can only be
 * linked out to (most Hudl share URLs are not embeddable).
 */
const embedUrl = (video) => {
  const url = video?.url || '';
  const yt = youTubeId(url);
  if (yt) return `https://www.youtube.com/embed/${yt}?rel=0&modestbranding=1`;
  const vm = vimeoId(url);
  if (vm) return `https://player.vimeo.com/video/${vm}`;
  if (url.includes('hudl.com/embed/')) return url;
  return null;
};

const formatHeight = (h) => (h && /^\d+-\d+$/.test(h) ? `${h.split('-')[0]}'${h.split('-')[1]}"` : h);

const refInput =
  'w-full bg-[#243B55] text-white px-3 py-2.5 rounded-lg border border-[#546E7A]/30 ' +
  'focus:border-[#00D4FF] focus:outline-none text-sm';

const SOURCE_LABELS = {
  self_reported: 'Self-reported',
  family: 'Self-reported',
  school_staff: 'Measured by school staff',
  camp: 'Camp verified',
  combine: 'Combine verified',
};

/* ─────────────────────────────────────────────────────────────────────────
   Small presentational pieces
   ───────────────────────────────────────────────────────────────────────── */

const Metric = ({ label, value, unit, accent, verified }) => {
  if (value === null || value === undefined || value === '') return null;
  return (
    <div className="rounded-xl border px-3 py-3.5 text-center relative"
      style={{
        background: accent ? 'rgba(0,212,255,0.07)' : 'rgba(36,59,85,0.45)',
        borderColor: accent ? 'rgba(0,212,255,0.35)' : 'rgba(84,110,122,0.25)',
      }}>
      <div className="font-rajdhani font-bold leading-none text-2xl sm:text-3xl"
        style={{ color: accent ? '#00D4FF' : '#FFFFFF' }}>
        {value}<span className="text-sm sm:text-base font-semibold ml-0.5">{unit}</span>
      </div>
      <div className="text-[#546E7A] text-[10px] uppercase tracking-[0.12em] mt-1.5 font-semibold">
        {label}
      </div>
      {verified && (
        <div className="flex items-center justify-center gap-1 mt-1.5">
          <ShieldCheck size={10} className="text-[#00E676]" />
          <span className="text-[#00E676] text-[9px] font-bold uppercase tracking-wide">Verified</span>
        </div>
      )}
    </div>
  );
};

const Section = ({ icon: Icon, title, children, right, id }) => (
  <section id={id} className="mb-8 section-anchor">
    <div className="flex items-center justify-between gap-3 mb-3.5">
      <h2 className="flex items-center gap-2 text-white font-bold text-base sm:text-lg">
        <Icon size={18} className="text-[#00D4FF]" />
        {title}
      </h2>
      {right}
    </div>
    {children}
  </section>
);

// Analytics is loaded by the server on live public profiles only, so on a
// preview link, or for a visitor who opted out, this does nothing.
const track = (event, params) => {
  try { if (typeof window.gtag === 'function') window.gtag('event', event, params); } catch { /* never break the page */ }
};

const VideoCard = ({ video, compact = false }) => {
  const src = embedUrl(video);
  const isUpload = video.source_type === 'upload' && video.file_path;
  // Uploaded film is hosted here: transcoded to H.264 so it plays in every
  // browser a coach may use, and served with byte-range support so it can be
  // seeked. Portrait sources are honored rather than letterboxed into 16:9.
  const isPortrait = !!(video.width && video.height && video.height > video.width);
  return (
    <div className="rounded-xl overflow-hidden border border-[#546E7A]/25 bg-[#1A2744]">
      {isUpload ? (
        <div className={isPortrait && !compact ? 'bg-black flex justify-center' : ''}>
        <video
          controls
          preload="metadata"
          playsInline
          data-clip-title={video.title}
          poster={video.poster_path ? `${API}${video.poster_path}` : undefined}
          className={`w-full bg-black ${isPortrait && !compact ? 'mx-auto' : ''}`}
          style={
            // In the portrait grid the column sets the width, so the clip
            // fills it at its own shape and the row stays level.
            compact
              ? { aspectRatio: `${video.width} / ${video.height}`, display: 'block' } :
            // Phone-shot film is portrait. Forcing 16:9 would letterbox it into
            // a thin strip, so honor the real shape and cap the height instead.
            isPortrait
              ? { maxHeight: '70vh', width: 'auto', maxWidth: '100%', display: 'block' }
              : { aspectRatio: video.width && video.height ? `${video.width} / ${video.height}` : '16 / 9' }
          }
        >
          <source src={`${API}${video.file_path}`} type="video/mp4" />
          Your browser cannot play this video.
        </video>
        </div>
      ) : src ? (
        <div className="relative w-full" style={{ paddingTop: '56.25%' }}>
          <iframe
            src={src}
            title={video.title}
            className="absolute inset-0 w-full h-full"
            style={{ border: 0 }}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            loading="lazy"
          />
        </div>
      ) : (
        <a href={video.url} target="_blank" rel="noopener noreferrer"
          className="flex items-center gap-3 px-4 py-5 active:bg-[#243B55]">
          <div className="w-11 h-11 rounded-full bg-[#00D4FF]/15 border border-[#00D4FF]/40 flex items-center justify-center flex-shrink-0">
            <Play size={18} className="text-[#00D4FF] ml-0.5" />
          </div>
          <div className="min-w-0">
            <div className="text-white font-semibold text-sm truncate">Watch on {video.provider === 'hudl' ? 'Hudl' : 'source'}</div>
            <div className="text-[#546E7A] text-xs truncate">{video.url}</div>
          </div>
          <ExternalLink size={15} className="text-[#546E7A] ml-auto flex-shrink-0" />
        </a>
      )}
      <div className={compact ? 'px-2.5 py-2' : 'px-4 py-3'}>
        <div className={`text-white font-semibold ${compact ? 'text-xs sm:text-sm' : 'text-sm'}`}>{video.title}</div>
        {video.description && (
          <div className="text-[#B0BEC5] text-xs mt-1 leading-relaxed">{video.description}</div>
        )}
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────────────
   Page
   ───────────────────────────────────────────────────────────────────────── */

const PublicProfile = () => {
  const { token } = useParams();
  // The server embeds the full profile in the HTML. Using it as initial state
  // means the page paints complete on first render, with no API round-trip --
  // which is what Googlebot's renderer sees, since it will not fetch URLs that
  // robots.txt disallows.
  const embedded = typeof window !== 'undefined' ? window.__PROFILE__ : null;
  const [profile, setProfile] = useState(embedded || null);
  const [state, setState] = useState(embedded ? 'ready' : 'loading'); // loading | ready | missing | error
  const [copied, setCopied] = useState(false);
  const [activeSection, setActiveSection] = useState(null);
  const navRef = useRef(null);
  const [refOpen, setRefOpen] = useState(false);
  const [refSent, setRefSent] = useState(false);
  const [refBusy, setRefBusy] = useState(false);
  const [refError, setRefError] = useState('');
  const [ref, setRef] = useState({
    author_name: '', author_title: '', author_organization: '',
    relationship: 'coach', body: '', author_email: '', website: '',
  });

  // What a visitor actually did: which clips they played and which links out
  // they followed. Only the kind of link is reported, never the address or
  // number behind it.
  useEffect(() => {
    const played = new Set();
    const onPlay = (e) => {
      const title = e.target?.dataset?.clipTitle;
      if (!title || played.has(title)) return;
      played.add(title);
      track('clip_play', { clip_title: title });
    };
    const onClick = (e) => {
      const a = e.target?.closest?.('a[href]');
      if (!a) return;
      const href = a.getAttribute('href') || '';
      const who = a.closest('[data-contact]')?.dataset.contact;
      if (href.startsWith('mailto:')) track('contact_click', { method: 'email', contact: who });
      else if (href.startsWith('tel:')) track('contact_click', { method: 'phone', contact: who });
      else if (/hudl\.com/i.test(href)) track('outbound_click', { destination: 'hudl' });
      else if (/maxpreps\.com/i.test(href)) track('outbound_click', { destination: 'maxpreps' });
      else if (/\/\/(x|twitter)\.com/i.test(href)) track('outbound_click', { destination: 'x' });
    };
    // Media events do not bubble, so listen in the capture phase.
    document.addEventListener('play', onPlay, true);
    document.addEventListener('click', onClick);
    return () => {
      document.removeEventListener('play', onPlay, true);
      document.removeEventListener('click', onClick);
    };
  }, []);

  useEffect(() => {
    // Already served with the page: nothing to fetch.
    if (embedded) return;

    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API}/api/public/${token}`);
        if (cancelled) return;
        if (res.status === 404) return setState('missing');
        if (!res.ok) return setState('error');
        setProfile(await res.json());
        setState('ready');
      } catch {
        // Never downgrade a rendered profile to an error screen.
        if (!cancelled) setState((prev) => (prev === 'ready' ? prev : 'error'));
      }
    })();
    return () => { cancelled = true; };
  }, [token, embedded]);

  // The page title is what shows in a browser tab and in a shared bookmark
  useEffect(() => {
    // The server already set a fuller title (name, position, class, school).
    // Only set one when arriving client-side, e.g. via an in-app link.
    if (profile?.name && !window.__PROFILE__) {
      document.title = `${profile.name} — ${profile.position || 'Prospect'} | SchemeSync HS`;
    }
  }, [profile]);

  // Track which section is on screen so the pinned tabs show where you are.
  useEffect(() => {
    if (state !== 'ready') return;
    const sections = Array.from(document.querySelectorAll('section[id]'));
    if (sections.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible.length) setActiveSection(visible[0].target.id);
      },
      // Bias the band toward the top of the viewport, just under the sticky bars.
      { rootMargin: '-140px 0px -55% 0px', threshold: 0 }
    );
    sections.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [state]);

  // A tab you cannot see is no use, so scroll the strip to the active one.
  useEffect(() => {
    if (!activeSection || !navRef.current) return;
    const chip = navRef.current.querySelector(`[data-nav="${activeSection}"]`);
    if (chip?.scrollIntoView) {
      chip.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
    }
  }, [activeSection]);

  const handleShare = useCallback(async () => {
    const url = window.location.href;
    const title = profile ? `${profile.name} — Recruiting Profile` : 'Recruiting Profile';
    if (navigator.share) {
      try { await navigator.share({ title, url }); return; } catch { /* dismissed */ }
    }
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* clipboard unavailable */ }
  }, [profile]);

  const submitReference = async (e) => {
    e.preventDefault();
    setRefError('');
    if (ref.author_name.trim().length < 2) return setRefError('Please enter your name.');
    if (ref.body.trim().length < 20) return setRefError('Please write at least a sentence or two.');
    setRefBusy(true);
    try {
      const res = await fetch(`${API}/api/public/${token}/testimonials`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(ref),
      });
      if (res.status === 429) {
        setRefError('Too many submissions from this connection. Please try again later.');
      } else if (!res.ok) {
        setRefError('Something went wrong. Please try again.');
      } else {
        setRefSent(true);
      }
    } catch {
      setRefError('Something went wrong. Please try again.');
    } finally {
      setRefBusy(false);
    }
  };

  if (state === 'loading') {
    return (
      <div className="min-h-screen bg-[#0A0E27] flex items-center justify-center px-4">
        <div className="text-[#00D4FF]">Loading profile…</div>
      </div>
    );
  }

  if (state === 'missing' || state === 'error') {
    return (
      <div className="min-h-screen bg-[#0A0E27] flex items-center justify-center px-4 text-center">
        <div>
          <div className="text-white text-xl font-bold mb-2">Profile unavailable</div>
          <div className="text-[#B0BEC5] text-sm max-w-sm">
            This recruiting profile doesn’t exist, or it isn’t published right now.
            Check the link, or ask whoever shared it for an updated one.
          </div>
        </div>
      </div>
    );
  }

  const p = profile;
  const sourceLabel = SOURCE_LABELS[p.measurables_source] || 'Self-reported';
  const featured = p.videos.find((v) => v.is_featured);
  const clips = p.videos.filter((v) => !v.is_featured);
  // Upright phone clips get their own tighter grid: side by side with wide
  // film they leave a ragged row, and one per column is a very long scroll.
  const isPortraitClip = (v) =>
    v.source_type === 'upload' && v.file_path && v.width && v.height && v.height > v.width;
  const portraitClips = clips.filter(isPortraitClip);
  const wideClips = clips.filter((v) => !isPortraitClip(v));
  const hasAcademics = p.gpa || p.sat_score || p.act_score || p.ncaa_id;
  const hasContact = p.coach_name || p.coach_email || p.coach_phone || p.coach_twitter || p.guardian_name;
  const location = [p.hometown, p.hs_state].filter(Boolean).join(', ');
  const hasFilm = featured || clips.length > 0 || p.hudl_url;
  const navSections = [
    { id: 'measurables', label: 'Measurables' },
    hasAcademics && { id: 'academics', label: 'Academics' },
    hasFilm && { id: 'film', label: 'Film' },
    p.public_bio && { id: 'about', label: 'About' },
    { id: 'references', label: 'References' },
    hasContact && { id: 'contact', label: 'Contact' },
  ].filter(Boolean);

  return (
    <div className="min-h-screen bg-[#0A0E27]">

      {/* Slim bar — this page has no app navigation by design */}
      <div className="app-bar app-chrome">
        <div className="max-w-4xl mx-auto flex items-center justify-between h-14 px-4">
          {/* Opens in a new tab: a coach following this out of curiosity should
              not lose the prospect page they came to read. */}
          <a
            href="https://aida-advisory.com/hs/"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="SchemeSync HS — learn more"
            className="flex items-center gap-2 min-w-0 tap no-underline"
          >
            <img src="/SchemeSync_Logo.png" alt="" className="h-7 w-auto flex-shrink-0" />
            <div className="text-sm truncate">
              <span className="font-bold text-white">Scheme</span>
              <span className="font-bold text-[#00D4FF]">Sync</span>
              <span className="font-bold text-[#00E676] ml-1 text-xs">HS</span>
            </div>
          </a>
          <button onClick={handleShare}
            className="flex items-center gap-1.5 text-xs font-semibold text-[#00D4FF] border border-[#00D4FF]/40 rounded-lg px-3 py-2">
            {copied ? <><Check size={14} /> Copied</> : <><Share2 size={14} /> Share</>}
          </button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6 sm:py-8">

        {/* ── Hero ─────────────────────────────────────────────── */}
        <div className="flex flex-col sm:flex-row items-center sm:items-start gap-5 mb-7 text-center sm:text-left">
          <div className="w-28 h-32 sm:w-32 sm:h-40 rounded-xl overflow-hidden flex-shrink-0
                          bg-[#12233D] border-2 border-[#546E7A]/30 flex items-center justify-center">
            {p.photo_url
              ? <img src={p.photo_url.startsWith('http') ? p.photo_url : `${API}${p.photo_url}`}
                  alt={p.name} className="w-full h-full object-cover" />
              : <span className="font-rajdhani font-bold text-3xl sm:text-4xl text-[#2A4060]">
                  {(p.name || '').split(' ').filter(Boolean).slice(0, 2).map((n) => n[0]).join('').toUpperCase()}
                </span>}
          </div>

          <div className="min-w-0 flex-1">
            <h1 className="text-white font-extrabold text-3xl sm:text-4xl leading-tight tracking-tight break-words">
              {p.name}
            </h1>

            <div className="flex items-center gap-2 mt-2.5 flex-wrap justify-center sm:justify-start">
              <span className="bg-[#00D4FF]/12 border border-[#00D4FF]/30 text-[#00D4FF]
                               rounded-md px-3 py-1 text-sm font-bold tracking-wide">
                {p.public_position_short || p.position}
                {p.jersey_number ? ` · #${p.jersey_number}` : ''}
              </span>
              {p.graduation_year && (
                <span className="bg-[#00E676]/12 border border-[#00E676]/30 text-[#00E676]
                                 rounded-md px-3 py-1 text-sm font-bold">
                  Class of {p.graduation_year}
                </span>
              )}
            </div>

            {/* An approved description of his position, when there is one,
                stands on its own: it is never shown beside a projection. */}
            {p.public_position_label && (
              <div className="text-white text-sm font-semibold mt-2.5">{p.public_position_label}</div>
            )}

            {!p.public_position_label && p.projected_positions && p.projected_positions !== p.position && (
              <div className="text-[#B0BEC5] text-sm mt-2.5">
                Projects at <span className="text-white font-semibold">{p.projected_positions}</span>
              </div>
            )}

            <div className="text-[#B0BEC5] text-sm mt-3 space-y-1">
              {(p.public_school_label || p.high_school) && (
                <div className="flex items-center gap-1.5 justify-center sm:justify-start">
                  <GraduationCap size={14} className="text-[#546E7A]" />
                  {p.public_school_label || p.high_school}{p.class_year ? ` · ${p.class_year}` : ''}
                </div>
              )}
              {location && (
                <div className="flex items-center gap-1.5 justify-center sm:justify-start">
                  <MapPin size={14} className="text-[#546E7A]" />
                  {location}
                </div>
              )}
            </div>

            {p.twitter_handle && (
              <a href={`https://x.com/${p.twitter_handle.replace('@', '')}`}
                target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-sm mt-3 font-semibold">
                <ExternalLink size={13} />
                {p.twitter_handle.startsWith('@') ? p.twitter_handle : `@${p.twitter_handle}`}
              </a>
            )}
          </div>
        </div>

        {/* Pinned section tabs. A coach should reach the film in one tap, and
            still be able to jump sections after scrolling. */}
        {navSections.length > 1 && (
          <nav className="section-nav app-chrome" aria-label="Sections">
            <div className="chip-row" ref={navRef}>
              {navSections.map(({ id, label }) => (
                <a
                  key={id}
                  href={`#${id}`}
                  data-nav={id}
                  aria-current={activeSection === id ? 'true' : undefined}
                  className={`chip flex-shrink-0 ${activeSection === id ? 'is-active' : ''}`}
                >
                  {label}
                </a>
              ))}
            </div>
          </nav>
        )}

        {p.public_headline && (
          <div className="rounded-xl border border-[#00D4FF]/25 bg-[#00D4FF]/[0.06] px-4 py-3.5 mb-7">
            <p className="text-white text-[15px] leading-relaxed font-medium">{p.public_headline}</p>
          </div>
        )}

        {/* ── Measurables ──────────────────────────────────────── */}
        <Section
          id="measurables"
          icon={Ruler}
          title="Measurables"
          right={
            <span className="flex items-center gap-1.5 text-[#546E7A] text-[11px] font-medium">
              <Info size={12} />
              {sourceLabel}
            </span>
          }
        >
          <div className="grid grid-cols-2 xs:grid-cols-3 sm:grid-cols-5 gap-2.5">
            {(p.measurables || []).map((m) => (
              <Metric
                key={m.field_key}
                label={m.display_name}
                value={m.field_key === 'height' ? formatHeight(m.value) : m.value}
                unit={m.unit === 'in' ? '"' : m.unit === 's' ? 's' : m.unit ? ` ${m.unit}` : ''}
                accent={m.is_core}
                verified={m.verified}
              />
            ))}
          </div>

          {p.measurables_event && (
            <div className="text-[#546E7A] text-xs mt-3 flex items-center gap-1.5">
              <Calendar size={12} /> {p.measurables_event}
              {p.measurables_measured_on ? ` · ${p.measurables_measured_on}` : ''}
            </div>
          )}
        </Section>

        {/* ── Academics ────────────────────────────────────────── */}
        {hasAcademics && (
          <Section id="academics" icon={GraduationCap} title="Academics">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
              <Metric label="GPA" value={p.gpa ? Number(p.gpa).toFixed(2) : null} accent />
              <Metric label="SAT" value={p.sat_score} />
              <Metric label="ACT" value={p.act_score} />
              {p.graduation_year && <Metric label="Grad Year" value={p.graduation_year} />}
            </div>
            {p.ncaa_id && (
              <div className="text-[#546E7A] text-xs mt-3 flex items-center gap-1.5">
                <ShieldCheck size={13} className="text-[#00E676]" />
                NCAA Eligibility Center ID: <span className="text-[#B0BEC5]">{p.ncaa_id}</span>
              </div>
            )}
          </Section>
        )}

        {/* ── Film ─────────────────────────────────────────────── */}
        {hasFilm && (
          <Section id="film" icon={Film} title="Film">
            {/* The featured film leads, then the short upright clips in the
                order the family set. */}
            {featured && <div className="mb-4"><VideoCard video={featured} /></div>}

            {portraitClips.length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5 sm:gap-3.5 mb-4">
                {portraitClips.map((v) => <VideoCard key={v.id} video={v} compact />)}
              </div>
            )}


            <div className="flex flex-col sm:flex-row gap-2.5 mb-4">
              {p.hudl_url && (
                <a href={p.hudl_url} target="_blank" rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 flex-1 bg-[#FF6B00]/12 border border-[#FF6B00]/40
                             text-[#FF8A3D] rounded-xl px-4 py-3.5 font-bold text-sm">
                  <Play size={16} /> Full Hudl Profile <ExternalLink size={14} />
                </a>
              )}
              {p.maxpreps_url && (
                <a href={p.maxpreps_url} target="_blank" rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 flex-1 bg-[#243B55]/60 border border-[#546E7A]/35
                             text-[#B0BEC5] rounded-xl px-4 py-3.5 font-bold text-sm">
                  MaxPreps Stats <ExternalLink size={14} />
                </a>
              )}
            </div>

            {wideClips.length > 0 && (
              <div className={`grid grid-cols-1 gap-3.5 ${wideClips.length > 1 ? 'md:grid-cols-2' : ''}`}>
                {wideClips.map((v) => <VideoCard key={v.id} video={v} />)}
              </div>
            )}
          </Section>
        )}

        {/* ── Bio ──────────────────────────────────────────────── */}
        {p.public_bio && (
          <Section id="about" icon={User} title="About">
            <div className="panel-card">
              <p className="text-[#C8D8E8] text-[15px] leading-relaxed whitespace-pre-line">{p.public_bio}</p>
            </div>
          </Section>
        )}

        {/* ── Contact ──────────────────────────────────────────── */}
        {hasContact && (
          <Section id="contact" icon={Mail} title="Contact">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {p.coach_name && (
                <div className="panel-card">
                  <div className="text-[#546E7A] text-[10px] uppercase tracking-[0.12em] font-bold mb-2">
                    {p.coach_title || 'Coach'}
                  </div>
                  <div className="text-white font-semibold">{p.coach_name}</div>
                  {p.coach_email && (
                    <a href={`mailto:${p.coach_email}`} data-contact="coach" className="flex items-center gap-2 text-sm mt-2">
                      <Mail size={13} /> {p.coach_email}
                    </a>
                  )}
                  {p.coach_phone && (
                    <>
                      <a href={`tel:${p.coach_phone}`} data-contact="coach" className="flex items-center gap-2 text-sm mt-1.5">
                        <Phone size={13} /> {p.coach_phone}
                      </a>
                      {p.coach_phone_note && (
                        <div className="text-[#546E7A] text-xs mt-1 ml-[21px]">{p.coach_phone_note}</div>
                      )}
                    </>
                  )}
                  {p.coach_twitter && (
                    <a href={`https://x.com/${p.coach_twitter.replace('@', '')}`}
                      target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-2 text-sm mt-1.5">
                      <ExternalLink size={13} />
                      {p.coach_twitter.startsWith('@') ? p.coach_twitter : `@${p.coach_twitter}`}
                    </a>
                  )}
                </div>
              )}
              {p.guardian_name && (
                <div className="panel-card">
                  <div className="text-[#546E7A] text-[10px] uppercase tracking-[0.12em] font-bold mb-2">
                    Parent / Guardian
                  </div>
                  <div className="text-white font-semibold">{p.guardian_name}</div>
                  {p.guardian_email && (
                    <a href={`mailto:${p.guardian_email}`} data-contact="guardian" className="flex items-center gap-2 text-sm mt-2">
                      <Mail size={13} /> {p.guardian_email}
                    </a>
                  )}
                  {p.guardian_phone && (
                    <a href={`tel:${p.guardian_phone}`} data-contact="guardian" className="flex items-center gap-2 text-sm mt-1.5">
                      <Phone size={13} /> {p.guardian_phone}
                    </a>
                  )}
                </div>
              )}
            </div>
          </Section>
        )}

        {/* ── References ───────────────────────────────────────── */}
        <Section id="references" icon={Quote} title="References">
          {p.testimonials && p.testimonials.length > 0 ? (
            <div className="space-y-3 mb-4">
              {p.testimonials.map((t) => (
                <figure key={t.id} className="panel-card">
                  <Quote size={16} className="text-[#00D4FF]/50 mb-2" />
                  <blockquote className="text-[#C8D8E8] text-[15px] leading-relaxed whitespace-pre-line">
                    {t.body}
                  </blockquote>
                  <figcaption className="mt-3 pt-3 border-t border-[#546E7A]/20">
                    <div className="text-white font-semibold text-sm">{t.author_name}</div>
                    <div className="text-[#546E7A] text-xs mt-0.5">
                      {[t.author_title, t.author_organization].filter(Boolean).join(' · ')}
                    </div>
                  </figcaption>
                </figure>
              ))}
            </div>
          ) : (
            <p className="text-[#546E7A] text-sm mb-4">
              No references published yet.
            </p>
          )}

          {refSent ? (
            <div className="rounded-xl border border-[#00E676]/30 bg-[#00E676]/[0.07] px-4 py-4 text-center">
              <Check size={20} className="text-[#00E676] mx-auto mb-2" />
              <div className="text-white font-semibold text-sm">Thank you</div>
              <div className="text-[#B0BEC5] text-xs mt-1">
                Your reference has been sent for review before it appears on this page.
              </div>
            </div>
          ) : !refOpen ? (
            <button onClick={() => setRefOpen(true)}
              className="flex items-center justify-center gap-2 w-full border border-[#00D4FF]/35
                         text-[#00D4FF] rounded-xl px-4 py-3.5 font-semibold text-sm">
              <MessageSquarePlus size={16} /> Leave a reference
            </button>
          ) : (
            <form onSubmit={submitReference} className="panel-card space-y-3">
              <p className="text-[#546E7A] text-xs leading-relaxed">
                For coaches, teachers and mentors. References are reviewed before
                they appear here. Your email is used only to verify who you are and
                is never shown publicly.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <input required className={refInput} placeholder="Your name *"
                  value={ref.author_name} maxLength={150}
                  onChange={(e) => setRef({ ...ref, author_name: e.target.value })} />
                <select className={refInput} value={ref.relationship}
                  onChange={(e) => setRef({ ...ref, relationship: e.target.value })}>
                  <option value="coach">Coach</option>
                  <option value="teacher">Teacher</option>
                  <option value="trainer">Trainer</option>
                  <option value="mentor">Mentor</option>
                  <option value="other">Other</option>
                </select>
                <input className={refInput} placeholder="Your title (e.g. Head Coach)"
                  value={ref.author_title} maxLength={150}
                  onChange={(e) => setRef({ ...ref, author_title: e.target.value })} />
                <input className={refInput} placeholder="School or organization"
                  value={ref.author_organization} maxLength={200}
                  onChange={(e) => setRef({ ...ref, author_organization: e.target.value })} />
              </div>

              <input type="email" className={refInput} placeholder="Your email (not shown publicly)"
                value={ref.author_email} maxLength={200}
                onChange={(e) => setRef({ ...ref, author_email: e.target.value })} />

              <textarea required rows={5} className={refInput} maxLength={2000}
                placeholder="What stands out about him as a player and as a person?"
                value={ref.body}
                onChange={(e) => setRef({ ...ref, body: e.target.value })} />

              {/* Honeypot: hidden from people, irresistible to bots */}
              <input type="text" name="website" tabIndex={-1} autoComplete="off"
                value={ref.website} aria-hidden="true"
                onChange={(e) => setRef({ ...ref, website: e.target.value })}
                style={{ position: 'absolute', left: '-9999px', width: 1, height: 1 }} />

              {refError && <div className="text-[#EF5350] text-sm">{refError}</div>}

              <div className="flex flex-col sm:flex-row gap-2">
                <button type="submit" disabled={refBusy}
                  className="btn-primary flex-1 flex items-center justify-center gap-2 disabled:opacity-50">
                  <Send size={15} /> {refBusy ? 'Sending…' : 'Submit reference'}
                </button>
                <button type="button" onClick={() => setRefOpen(false)}
                  className="btn-secondary sm:flex-none">Cancel</button>
              </div>
            </form>
          )}
        </Section>

        <footer className="border-t border-[#546E7A]/20 pt-5 mt-10 text-center">
          <div className="text-[#546E7A] text-xs">
            Recruiting profile powered by{' '}
            <a href="https://aida-advisory.com/hs/" target="_blank" rel="noopener noreferrer"
              className="text-[#B0BEC5] font-semibold">SchemeSync HS</a>
          </div>
          <div className="text-[#37474F] text-[11px] mt-1.5">
            Measurables are {sourceLabel.toLowerCase()}.
          </div>
        </footer>
      </div>
    </div>
  );
};

export default PublicProfile;
